/**
 * PlanPanel — canvas view for the Plan workspace.
 *
 * Displays the project objective, plan sections with items, and uploaded documents.
 * Updated in real-time via WORKSPACE_UPDATE WebSocket events.
 */

import React from 'react';
import { useProjectStore } from '../../stores/projectStore';
import { ClipboardList, FileText, CheckCircle2, Circle, ArrowRight, Loader2 } from 'lucide-react';
import type { PlanItemStatus } from '../../types/workspace';

const STATUS_ICON: Record<PlanItemStatus, React.ReactNode> = {
  planned: <Circle className="w-3.5 h-3.5 text-fg-tertiary" />,
  active: <Loader2 className="w-3.5 h-3.5 text-accent animate-spin" />,
  done: <CheckCircle2 className="w-3.5 h-3.5 text-success" />,
  revised: <ArrowRight className="w-3.5 h-3.5 text-warning" />,
};

export default function PlanPanel() {
  const { plan } = useProjectStore();

  return (
    <div className="h-full overflow-y-auto p-6 bg-bg-warm">
      {/* Header */}
      <div className="flex items-center gap-2 mb-6">
        <ClipboardList className="w-5 h-5 text-accent" />
        <h2 className="text-lg font-semibold text-fg">Project plan</h2>
        {plan.is_ready && (
          <span className="ml-auto text-xs font-medium text-success bg-success-bg px-2 py-0.5 rounded-full">
            Ready
          </span>
        )}
      </div>

      {/* Objective */}
      {plan.objective ? (
        <div className="mb-6 p-4 bg-accent-tint rounded-lg border border-accent-border">
          <div className="text-xs font-medium text-accent uppercase tracking-wide mb-1">Objective</div>
          <div className="text-sm text-fg-secondary">{plan.objective}</div>
        </div>
      ) : (
        <div className="mb-6 p-4 bg-bg-warm rounded-lg border border-border-default text-sm text-fg-tertiary italic">
          No objective set yet. The agent will help you define one.
        </div>
      )}

      {/* Plan Sections */}
      {plan.sections.length > 0 ? (
        <div className="space-y-4">
          {plan.sections.map((section) => (
            <div key={section.section_id} className="border border-border-default rounded-lg overflow-hidden">
              <div className="px-4 py-2.5 bg-bg-warm border-b border-border-default flex items-center justify-between">
                <span className="text-sm font-medium text-fg-secondary">{section.title}</span>
                <span className="text-xs text-fg-tertiary">{section.target_phase}</span>
              </div>
              <div className="p-3">
                {section.items.length > 0 ? (
                  <ul className="space-y-2">
                    {section.items.map((item) => (
                      <li key={item.item_id} className="flex items-start gap-2">
                        <span className="mt-0.5 flex-shrink-0">{STATUS_ICON[item.status]}</span>
                        <div className="flex-1 min-w-0">
                          <span className={`text-sm ${item.status === 'done' ? 'text-fg-tertiary line-through' : 'text-fg-secondary'}`}>
                            {item.text}
                          </span>
                          {item.revision_note && (
                            <div className="text-xs text-warning mt-0.5 italic">{item.revision_note}</div>
                          )}
                          {item.phase_origin && item.phase_origin !== 'plan' && (
                            <span className="text-xs text-fg-tertiary ml-1">({item.phase_origin})</span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs text-fg-tertiary italic py-1">No items yet</div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-fg-tertiary italic">
          Plan sections will appear here as the conversation progresses.
        </div>
      )}

      {/* Documents */}
      {plan.documents.length > 0 && (
        <div className="mt-6">
          <div className="text-xs font-medium text-fg-tertiary uppercase tracking-wide mb-2">Documents</div>
          <div className="space-y-2">
            {plan.documents.map((doc) => (
              <div key={doc.document_id} className="flex items-center gap-2 p-2 bg-bg-warm rounded border border-border-default">
                <FileText className="w-4 h-4 text-fg-tertiary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-fg-secondary truncate">{doc.filename}</div>
                  {doc.summary && <div className="text-xs text-fg-tertiary truncate">{doc.summary}</div>}
                </div>
                <span className={`text-xs px-1.5 py-0.5 rounded ${doc.status === 'ready' ? 'text-success bg-success-bg' : 'text-fg-tertiary bg-bg-warm'}`}>
                  {doc.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
